import type { ReactNode } from 'react';
import type { MyDecision } from '../../api/cabinet';
import { api } from '../../api/client';
import { usePagedLoader } from '../../api/paging';
import {
  MODERATION_TARGET_LABELS,
  type ModerationTarget,
  type ModerationVerdict,
  type Page,
} from '../../api/types';
import { TASK_QUEUE_LABELS } from '../../app/router';
import { t } from '../../i18n';
import { docNumber } from '../../shared/lib/docNumber';
import { useKeptState } from '../../shared/lib/keptState';
import {
  CardList,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterSelect,
  LoadingState,
  Section,
  type FilterOption,
} from '../../shared/ui';
import { ShowMore, formatDateTime, personName } from '../review/staffKit';
import { DecisionCard } from './DecisionCard';

/**
 * Журнал решений модерации: кто, когда и что решил по анкетам, предметам и
 * способностям. Нужен не ради отчётности, а чтобы на вопрос игрока «почему
 * мне отказали полгода назад» было куда посмотреть, не поднимая переписку.
 *
 * Раздел вкладки «Задачи», а не отдельная панель: свободного идентификатора
 * панели в router.ts нет, а трогать роутер — не наша зона.
 */

/** Строка журнала: то же решение, что видит игрок, плюс персонаж и предмет спора. */
interface DecisionRow extends MyDecision {
  character: { id: number; name: string | null } | null;
}

type TargetFilter = 'all' | ModerationTarget;
type VerdictFilter = 'all' | ModerationVerdict;

const TARGET_FILTERS: readonly FilterOption<TargetFilter>[] = [
  { value: 'all', label: t('decision.filter.allTargets') },
  { value: 'form', label: TASK_QUEUE_LABELS.form },
  { value: 'item', label: TASK_QUEUE_LABELS.item },
  { value: 'pet', label: TASK_QUEUE_LABELS.pet },
  { value: 'ability', label: TASK_QUEUE_LABELS.ability },
];

const VERDICT_FILTERS: readonly FilterOption<VerdictFilter>[] = [
  { value: 'all', label: t('decision.filter.anyVerdict') },
  { value: 'approve', label: t('decision.filter.approved') },
  { value: 'reject', label: t('decision.filter.rejected') },
];

export function DecisionLogSection({ tabs, active }: { tabs: ReactNode; active: boolean }) {
  const [target, setTarget] = useKeptState<TargetFilter>('tasks.log.target', 'all');
  const [verdict, setVerdict] = useKeptState<VerdictFilter>('tasks.log.verdict', 'all');

  const log = usePagedLoader<DecisionRow>(
    (params, signal) =>
      api.get<Page<DecisionRow>>(
        '/api/decisions',
        {
          target: target === 'all' ? undefined : target,
          verdict: verdict === 'all' ? undefined : verdict,
          limit: params.limit,
          offset: params.offset,
        },
        signal,
      ),
    [target, verdict],
    active,
    'tasks.log',
  );

  return (
    <>
      <Section>
        {tabs}
        <FilterBar>
          <FilterSelect
            label={t('decision.filter.target')}
            options={TARGET_FILTERS}
            value={target}
            onChange={setTarget}
          />
          <FilterSelect
            label={t('decision.filter.verdict')}
            options={VERDICT_FILTERS}
            value={verdict}
            onChange={setVerdict}
          />
        </FilterBar>
      </Section>

      <Section>
        {log.loading && !log.data ? <LoadingState text={t('decision.log.loading')} /> : null}
        {log.error ? <ErrorState message={log.error} onRetry={log.reload} /> : null}

        {log.data && log.data.items.length === 0 ? (
          <EmptyState title={t('decision.log.empty.title')}>
            {t('decision.log.empty.text')}
          </EmptyState>
        ) : null}

        <CardList>
          {log.data?.items.map((row) => (
            <LogCard key={row.id} row={row} />
          ))}
        </CardList>

        <ShowMore page={log.data} onMore={log.loadMore} loading={log.loading} />
      </Section>
    </>
  );
}

function LogCard({ row }: { row: DecisionRow }) {
  const approved = row.verdict === 'approve';
  const subject = row.subject?.trim() || null;
  // Персонажа могли удалить — решение его переживает: строка остаётся без имени.
  const characterName = row.character?.name?.trim() || null;
  const title =
    subject ??
    characterName ??
    `${MODERATION_TARGET_LABELS[row.target]} ${docNumber(row.targetId)}`;

  return (
    <DecisionCard
      approved={approved}
      title={title}
      target={MODERATION_TARGET_LABELS[row.target]}
      // Персонаж — то единственное, чем стаффовая карточка отличается от
      // игроцкой. Не называем его второй раз ТОЛЬКО у анкеты: заголовок там
      // и есть имя персонажа, даже когда сервер прислал его в `subject`.
      // Сравнивать строки нельзя — питомца зовут так же, как хозяина чаще,
      // чем кажется, и имя персонажа пропадало из карточки на ровном месте.
      character={row.target === 'form' ? null : characterName}
      date={formatDateTime(row.decidedAt)}
      comment={row.comment}
      moderator={personName(row.moderator)}
    />
  );
}
